import React from 'react';
import { Box, Typography } from '@mui/material';
import styles from './AISummaryDetails.module.scss';
import { AISummaryDetailsProps } from './types';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { AISummaryStartForm } from './components/AISummaryStartForm';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAISummary } from '../../hooks/useAISummary';
import { useAISummaryDialog } from '../../../../common/ai-summary-dialog/useAISummaryDialog';
import AISummaryDialog from '../../../../common/ai-summary-dialog/AISummaryDialog';
import { PacmanLoaderBlock } from '../../../../common/pacman-loader';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { RootState } from '../../../../store';

const AISummaryDetails = ({
  pendingStatus,
  doc,
  allowGettingAISummary,
  isImage,
  aiSummaryCreationMode: aiSummaryCreationModeProp,
  setAISummaryCreationMode: setAISummaryCreationModeProp,
}: AISummaryDetailsProps) => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const contractGenerated = params.get('contractGenerated');
  const generateSummaryParam = params.get('generateSummary');
  const { documentId } = useParams();
  const aiSummaryGenerated = useAppSelector(
    (state: RootState) => state.currentDocumentAISummary.summary
  );
  const aiAssistProp = contractGenerated
    ? 'open_ai_assist_document_contract'
    : 'open_ai_assist_document_upload';
  const aiAssistIds = doc?.[aiAssistProp] ?? [];
  const aiAssistId = aiAssistIds.length > 0 ? Math.max(...aiAssistIds) : undefined;
  const navigate = useNavigate();
  const {
    summary,
    pendingStatus: pendingStatusAISummary,
    aiSummaryCreationMode,
    setAISummaryCreationMode,
  } = useAISummary(aiAssistId, allowGettingAISummary);
  const {
    dialogOpen: aiSummaryDialogOpen,
    setDocument: setDocumentForAISummary,
    handleAnalyze: handleAnalyzeFunction,
    documentType,
    setDocumentType,
    purpose,
    setPurpose,
    purposes,
    resultFormat,
    setResultFormat,
    contextualInformation,
    setContextualInformation,
    simplifyJargon,
    setSimplifyJargon,
    detailLevel,
    setDetailLevel,
    analysisLoading,
    setAnalysisLoading,
  } = useAISummaryDialog();

  return (
    <>
      <Box className={styles.rightSection}>
        <Box className={styles.aiSummaryHeader}>
          <Typography className={styles.aiSummaryTitle} variant='h6'>
            {t('headers.aiSummary')}
          </Typography>
        </Box>
        <Box className={styles.aiSummaryContent}>
          {(summary || aiSummaryGenerated) &&
          !aiSummaryCreationMode &&
          !aiSummaryCreationModeProp ? (
            parse(aiSummaryGenerated?.content || summary)
          ) : !summary && !aiSummaryCreationMode && !aiSummaryCreationModeProp ? (
            <AISummaryStartForm
              buttonDisabled={isImage}
              handleBtnClick={() => {
                setAISummaryCreationMode(true);
                setAISummaryCreationModeProp(true);
              }}
            />
          ) : null}
          {(aiSummaryCreationMode || aiSummaryCreationModeProp) && (
            <AISummaryDialog
              open={aiSummaryDialogOpen}
              //documentName={documentForAISummary.name}
              onClose={() => {
                setDocumentForAISummary(null);
                setAISummaryCreationMode(false);
                setAISummaryCreationModeProp(false);
                setAISummaryCreationModeProp(false);
                if (generateSummaryParam) {
                  navigate(window.location.pathname + '?initialTab=1');
                }
              }}
              simplifyJargon={simplifyJargon}
              setSimplifyJargon={setSimplifyJargon}
              setDocumentType={setDocumentType}
              documentType={documentType}
              resultFormat={resultFormat}
              setResultFormat={setResultFormat}
              purpose={purpose}
              setPurpose={setPurpose}
              purposes={purposes}
              contextualInformation={contextualInformation}
              setContextualInformation={setContextualInformation}
              detailLevel={detailLevel}
              setDetailLevel={setDetailLevel}
              handleAnalyzeFunction={() =>
                handleAnalyzeFunction({
                  handleAnalyzeSuccess: () => {
                    setAISummaryCreationMode(false);
                    setAISummaryCreationModeProp(false);
                    setDocumentForAISummary(null);
                    setAnalysisLoading(false);
                  },
                  handleAnalyzeError: undefined,
                  isDocumentContract: Boolean(contractGenerated),
                  documentId: documentId ?? '',
                })
              }
            />
          )}
          {analysisLoading && <PacmanLoaderBlock loading={analysisLoading} />}
        </Box>
        {(pendingStatus || pendingStatusAISummary) && <ProgressOverlay position='absolute' />}
      </Box>
    </>
  );
};

export default AISummaryDetails;
